import {saveAs} from 'file-saver';
import pick from 'lodash/pick';

import {showNewNotification} from '@edna/components';
import {and, formatDate} from '@edna/utils';

import {TData, TPayload} from 'src/components/List';
import {TItemId, XLSX_MIME_TYPE} from 'src/constants';
import {CUSTOM_TAX_RATE, TAgentClient, TAgentFormData} from 'src/containers/LeadCompanyClients';
import {ERoles} from 'src/containers/User';
import {CACHE_LIFETIME, ECacheTag, listenerMiddleware, rootApi} from 'src/models';
import {mapEmptyFieldsToUndefined} from 'src/utils';

import {
  TAgentAccountingDocument,
  TAgentReport,
  TAgentReportPayments,
  TLeadContact,
  TReportFilters,
} from './definitions';

const prepareReportFilters = (payload: TReportFilters) => {
  const {period, ...other} = payload;

  return {
    ...other,
    timeTo: period?.stopDate,
    timeFrom: period?.startDate,
  };
};

type TFirstLogin = {
  firstLogin: boolean;
};

const prepareAgentFormValues = (values: Partial<TAgentFormData & TFirstLogin>) => {
  const customTaxRate = (values.customTaxRate || 0) / 100;
  const taxRate = values.taxRate === CUSTOM_TAX_RATE ? customTaxRate : values.taxRate;

  const restValues = pick(values, [
    'companyName',
    'signatoryLastName',
    'signatoryFirstName',
    'signatoryMiddleName',
    'signingBasis',
    'signingBasisNumber',
    'signingBasisDate',
    'firstLogin',
  ]);

  return mapEmptyFieldsToUndefined({
    ...restValues,
    taxRate,
  });
};

const getRoles = (isManagerCondition: boolean) =>
  isManagerCondition ? and<ERoles>('ROLE_ACCOUNT_MANAGER') : and<ERoles>('ROLE_AGENT');

const api = rootApi.injectEndpoints({
  overrideExisting: 'throw',
  endpoints: (builder) => ({
    getAgentUser: builder.query<TAgentClient & TFirstLogin, void>({
      query: () => ({
        method: 'GET',
        url: '/tenantmanagement/agent',
        meta: {roles: and<ERoles>('ROLE_AGENT')},
      }),
      providesTags: [ECacheTag.USER],
    }),
    editAgentUser: builder.mutation<TAgentClient, Partial<TAgentFormData & TFirstLogin>>({
      query: (data) => ({
        method: 'PUT',
        data: prepareAgentFormValues(data),
        url: '/tenantmanagement/agent',
        meta: {roles: and<ERoles>('ROLE_AGENT')},
      }),
      invalidatesTags: [ECacheTag.USER],
    }),
    saveLeadContact: builder.mutation<void, TLeadContact>({
      query: ({id, ...data}) => ({
        method: id ? 'PUT' : 'POST',
        url: id ? `/tenantmanagement/tenant-lead/${id}` : '/tenantmanagement/tenant-lead/draft',
        data: {
          ...data,
          comment: data.comment ?? '',
          email: data.email ?? '',
        },
        meta: {
          isShowError: true,
          errorMessageKey: 'List:errors.failedToSaveItem',
          isShowSuccess: true,
          successMessageKey: 'List:notification.saveSuccess',
          meta: {roles: getRoles(!!id)},
        },
      }),
      invalidatesTags: [ECacheTag.LEAD_CLIENTS],
    }),
    getAgentReport: builder.query<TData<TAgentReport>, TPayload<TReportFilters>>({
      query: ({agentId, ...data}) => ({
        method: 'POST',
        data: prepareReportFilters(data),
        params: {agentId},
        url: agentId ? '/report/agent/manager' : '/report/agent',
        meta: {roles: getRoles(!!agentId)},
      }),
      transformResponse: (data: TData<TAgentReport>) =>
        data
          ? {
              ...data,
              content: data.content.map((item) => ({...item, id: item.transactionId})),
            }
          : data,
      keepUnusedDataFor: CACHE_LIFETIME,
      providesTags: (result) => {
        if (!result) {
          return [ECacheTag.AGENT_REPORTS];
        }

        return [
          ECacheTag.AGENT_REPORTS,
          ...result.content.map(({id}) => ({id, type: ECacheTag.AGENT_REPORTS})),
        ];
      },
    }),
    getAgentReportTotal: builder.query<number, TPayload<TReportFilters>>({
      query: ({agentId, ...data}) => ({
        method: 'POST',
        data: prepareReportFilters(data),
        params: {agentId},
        url: agentId ? '/report/agent/manager/total' : '/report/agent/total',
        meta: {roles: getRoles(!!agentId)},
      }),
      keepUnusedDataFor: CACHE_LIFETIME,
      providesTags: [ECacheTag.AGENT_REPORTS],
    }),
    getAgentReportPayments: builder.query<TAgentReportPayments, TPayload<TReportFilters>>({
      query: ({agentId, ...data}) => ({
        method: 'POST',
        data: prepareReportFilters(data),
        params: {agentId},
        url: agentId ? '/report/agent/manager/payments-total' : '/report/agent/payments-total',
        meta: {roles: getRoles(!!agentId)},
      }),
      keepUnusedDataFor: CACHE_LIFETIME,
      providesTags: [ECacheTag.AGENT_REPORTS],
    }),
    downloadAgentReport: builder.mutation<Blob, TPayload<TReportFilters>>({
      query: ({agentId, ...data}) => ({
        method: 'POST',
        data: prepareReportFilters(data),
        params: {agentId},
        url: agentId ? '/report/agent/manager/download' : '/report/agent/download',
        responseType: 'blob',
        meta: {
          isShowError: true,
          errorMessageKey: 'Common:errorDownload',
          roles: getRoles(!!agentId),
        },
      }),
    }),
    getAgentAccountingDocuments: builder.query<TAgentAccountingDocument[], void | TItemId>({
      query: (agentId) => ({
        method: 'GET',
        url: agentId ? '/report/agent/manager/uploads' : '/report/agent/uploads',
        params: {agentId},
        meta: {roles: getRoles(!!agentId)},
      }),
      keepUnusedDataFor: CACHE_LIFETIME,
    }),
  }),
});

listenerMiddleware.startListening({
  matcher: api.endpoints.downloadAgentReport.matchFulfilled,
  effect: (action) => {
    const date = formatDate(new Date(), {format: 'yyyy-MM-dd'});

    saveAs(new File([action.payload], `report-${date}.xlsx`, {type: XLSX_MIME_TYPE}));

    showNewNotification({
      type: 'SUCCESS',
      messageKey: 'Common:successDownload',
    });
  },
});

export const {
  useGetAgentUserQuery,
  useEditAgentUserMutation,
  useSaveLeadContactMutation,
  useGetAgentReportQuery,
  useGetAgentReportTotalQuery,
  useGetAgentReportPaymentsQuery,
  useDownloadAgentReportMutation,
  useGetAgentAccountingDocumentsQuery,
  endpoints: agentEndpoints,
} = api;
